// Import the functions you need from the SDKs you need

import { initializeApp, getApps } from "firebase/app";
import { getDatabase, ref, onValue, runTransaction } from "firebase/database";
import { initializeAppCheck, ReCaptchaV3Provider, getToken as getAppCheckToken } from "firebase/app-check";

import { firebaseConfig } from "@/config/firebase-config";
import { CLAPS_RATE_WINDOW_MS, MAX_CLAPS_PER_CLIENT_TOTAL, MAX_CLAPS_PER_MINUTE } from "@/config/config";

function getClientId () {
  let id = localStorage.getItem(process.env.VUE_APP_KKW_CLAP_CLIENT_ID_STORAGE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(process.env.VUE_APP_KKW_CLAP_CLIENT_ID_STORAGE_KEY, id);
  }
  return id;
}

// region Initialize Firebase
const apps = getApps();
const FirebaseApp = apps.length < 1 ? initializeApp(firebaseConfig) : apps[0];

// Initialize Realtime Database and get a reference to the service
const realtimeDB = getDatabase(FirebaseApp);

if (process.env.NODE_ENV === "development") {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.VUE_APP_KKW_A_C_DT;
}

// Initialize Firebase App Check
const appCheckInit = initializeAppCheck(FirebaseApp,  {
  provider: new ReCaptchaV3Provider(process.env.VUE_APP_RECAPTCHA_SITE_KEY),
  isTokenAutoRefreshEnabled: true // Automatically refresh App Check tokens
});

/**
 * Real-time listener for post claps
 * @param {string} slug - unique slug for blog post
 * @param {(count: { total: number, clients: object }) => void} callback - called when claps change
 */
export function getPostClapsInRealTime (slug, callback) {
  const clapRef = ref(realtimeDB, `posts/${slug}/claps`);
  onValue(clapRef, (snapshot) => {
    const { total: count } = snapshot.val() || { "total": 0, "clients": {} }
    callback(count);
  });
}

/**
 * Increment claps for a blog post
 * @param {string} slug - unique slug for blog post
 */
export async function updatePostClaps (slug) {
  const clientId = getClientId();
  const now = Date.now();

  // const { token } = await getAppCheckToken(appCheckInit, true);
  const clapRef = ref(realtimeDB, `posts/${slug}/claps`);

  runTransaction(clapRef, (current) => {
    if (current === null) {
      current = { total: 0, clients: {} };
    } else if (typeof current === "number") {
      // Old schema: just a number
      current = { total: current, clients: {} };
    }

    if (!current.clients) {
      current.clients = {};
    }

    const prevClient = current.clients[clientId] || {
      count: 0,
      lastTs: 0,
      windowStart: 0,
      windowCount: 0,
    };

    // reset rolling window if needed
    if (now - (prevClient.windowStart || 0) > CLAPS_RATE_WINDOW_MS) {
      prevClient.windowStart = now;
      prevClient.windowCount = 0;
    }

    // enforce per-minute rate
    if (prevClient.windowCount >= MAX_CLAPS_PER_MINUTE) {
      return; // abort transaction
    }

    // enforce total per-client
    if (prevClient.count >= MAX_CLAPS_PER_CLIENT_TOTAL) {
      return "Aborted transaction: total claps exceeded."
    }

    const oldCount = prevClient.count;
    prevClient.count = Math.min(MAX_CLAPS_PER_CLIENT_TOTAL, oldCount + 1);
    prevClient.windowCount = (prevClient.windowCount || 0) + 1;
    prevClient.lastTs = now;

    current.clients[clientId] = prevClient;
    current.total = (current.total || 0) + (prevClient.count - oldCount);

    return current;
  });
}

export async function sendClap (postSlug) {
  const clientId = getClientId();

  const { token } = await getAppCheckToken(appCheckInit, true);
  const url = `${process.env.VUE_APP_FIREBASE_CLOUD_FUNCTIONS_URL}/incrementClap`
  console.log({
    clientId,
    token,
    url
  });
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Firebase-AppCheck": token,
    },
    body: JSON.stringify({ postSlug, clientId }),
  });

  const json = await resp.json();
  if (!resp.ok) throw new Error(json.error);
  return json.total;
}
